/**
 * 習慣の完了/未完了を切り替えるカスタムフック
 *
 * 楽観的更新（Optimistic Update）パターンを実装している。
 *
 * 楽観的更新とは:
 * 通常、サーバーにリクエストを送信し、成功レスポンスを待ってからUIを更新する。
 * しかしこれではユーザー体験が悪くなる（チェックを入れても反映が遅い）。
 *
 * 楽観的更新では:
 * 1. 先にUI を更新（楽観的に成功を仮定）
 * 2. バックグラウンドでサーバーにリクエスト
 * 3. 失敗した場合のみ元の状態に戻す
 *
 * これにより、ユーザーは即座にフィードバックを得られる。
 *
 * Rails で言えば:
 * - Turbo の楽観的更新や StimulusReflex のオプティミスティックモードに相当
 *
 * @example
 * const { toggle, isPending } = useHabitToggle({
 *   onError: (error) => console.error(error)
 * })
 *
 * // 習慣の完了状態を切り替え
 * toggle(habitId, '2024-01-15', false, (newCompleted) => {
 *   // UIを即座に更新
 *   setCompleted(newCompleted)
 * })
 *
 * // ローディング状態の確認
 * if (isPending(habitId, '2024-01-15')) {
 *   // ローディング表示
 * }
 */

import { useCallback, useState } from 'react'
import { router } from '@inertiajs/react'
import * as Routes from '@/lib/routes'
import type { HabitLogData } from '@/types'

interface UseHabitToggleOptions {
  /** エラー発生時のコールバック */
  onError?: (error: Error) => void
}

/** 各習慣×日付の処理中状態を管理する型 */
interface ToggleState {
  [key: string]: boolean
}

/** ログモーダルの対象情報 */
export interface LogModalTarget {
  habitId: number
  habitName: string
  date: string
  existingLog?: HabitLogData | null
  mode: 'new' | 'edit'
}

export function useHabitToggle(options: UseHabitToggleOptions = {}) {
  // 習慣ID×日付をキーとして、処理中かどうかを管理
  const [pendingToggles, setPendingToggles] = useState<ToggleState>({})
  const [logModalTarget, setLogModalTarget] = useState<LogModalTarget | null>(
    null
  )

  const hapticFeedback = useCallback(() => {
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate(10)
    }
  }, [])

  const toggle = useCallback(
    async (
      habitId: number,
      date: string,
      currentCompleted: boolean,
      onOptimisticUpdate: (completed: boolean) => void,
      loggingOptions?: {
        enabled: boolean
        habitName: string
        existingLog?: HabitLogData | null
      }
    ) => {
      const key = `${habitId}-${date}`

      if (pendingToggles[key]) {
        return
      }

      hapticFeedback()

      // 完了済み + ログ有効 → トグルせず編集モーダルを開く
      if (currentCompleted && loggingOptions?.enabled) {
        setLogModalTarget({
          habitId,
          habitName: loggingOptions.habitName,
          date,
          existingLog: loggingOptions.existingLog,
          mode: 'edit',
        })
        return
      }

      const newCompleted = !currentCompleted
      onOptimisticUpdate(newCompleted)

      setPendingToggles((prev) => ({ ...prev, [key]: true }))

      try {
        await new Promise<void>((resolve, reject) => {
          router.post(
            Routes.toggle_habit_path(habitId),
            { date },
            {
              preserveScroll: true,
              onSuccess: () => {
                // 完了に切り替えた場合 && ログ機能が有効 → モーダル表示
                if (newCompleted && loggingOptions?.enabled) {
                  setLogModalTarget({
                    habitId,
                    habitName: loggingOptions.habitName,
                    date,
                    existingLog: loggingOptions.existingLog,
                    mode: 'new',
                  })
                }
                resolve()
              },
              onError: (errors) =>
                reject(new Error(Object.values(errors).join(', '))),
            }
          )
        })
      } catch (error) {
        onOptimisticUpdate(currentCompleted)
        options.onError?.(
          error instanceof Error ? error : new Error('Toggle failed')
        )
      } finally {
        setPendingToggles((prev) => {
          const newState = { ...prev }
          delete newState[key]
          return newState
        })
      }
    },
    [hapticFeedback, options, pendingToggles]
  )

  const isPending = useCallback(
    (habitId: number, date: string) => {
      return !!pendingToggles[`${habitId}-${date}`]
    },
    [pendingToggles]
  )

  const closeLogModal = useCallback(() => {
    setLogModalTarget(null)
  }, [])

  const saveLog = useCallback(
    async (habitId: number, date: string, data: HabitLogData) => {
      await new Promise<void>((resolve, reject) => {
        router.patch(
          Routes.update_log_habit_path(habitId),
          {
            date,
            started_at: data.startedAt,
            duration_minutes: data.durationMinutes,
            note: data.note,
          },
          {
            preserveScroll: true,
            onSuccess: () => resolve(),
            onError: (errors) =>
              reject(new Error(Object.values(errors).join(', '))),
          }
        )
      })
      setLogModalTarget(null)
    },
    []
  )

  const uncomplete = useCallback(
    (
      habitId: number,
      date: string,
      onOptimisticUpdate: (completed: boolean) => void
    ) => {
      onOptimisticUpdate(false)
      setLogModalTarget(null)
      router.post(
        Routes.toggle_habit_path(habitId),
        { date },
        {
          preserveScroll: true,
          onError: () => onOptimisticUpdate(true),
        }
      )
    },
    []
  )

  return {
    toggle,
    isPending,
    logModalTarget,
    closeLogModal,
    saveLog,
    uncomplete,
  }
}
